/**
 * AUTH CONTEXT
 * Global authentication state management
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(() => {
        const stored = localStorage.getItem('cr_user');
        return stored ? JSON.parse(stored) : null;
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const token = localStorage.getItem('cr_token');
        if (token) {
            authAPI.getMe()
                .then(res => setUser(res.data.user))
                .catch(() => {
                    localStorage.removeItem('cr_token');
                    localStorage.removeItem('cr_user');
                    setUser(null);
                })
                .finally(() => setLoading(false));
        } else {
            setLoading(false);
        }
    }, []);

    const login = async (email, password) => {
        const res = await authAPI.login(email, password);
        localStorage.setItem('cr_token', res.data.token);
        localStorage.setItem('cr_user', JSON.stringify(res.data.user));
        setUser(res.data.user);
        return res.data;
    };

    const register = async (data) => {
        const res = await authAPI.register(data);
        return res.data;
    };

    const verifyEmail = async (email, code) => {
        const res = await authAPI.verifyEmail(email, code);
        localStorage.setItem('cr_token', res.data.token);
        localStorage.setItem('cr_user', JSON.stringify(res.data.user));
        setUser(res.data.user);
        return res.data;
    };

    const resendVerification = async (email) => {
        const res = await authAPI.resendVerification(email);
        return res.data;
    };

    const verifyAdminMfa = async (email, code) => {
        const res = await authAPI.verifyAdminMfa(email, code);
        localStorage.setItem('cr_token', res.data.token);
        localStorage.setItem('cr_user', JSON.stringify(res.data.user));
        setUser(res.data.user);
        return res.data;
    };

    const logout = () => {
        localStorage.removeItem('cr_token');
        localStorage.removeItem('cr_user');
        setUser(null);
    };

    const updateUser = (userData) => {
        const currentUser = JSON.parse(localStorage.getItem('cr_user') || '{}');
        const updatedUser = { ...currentUser, ...userData };
        localStorage.setItem('cr_user', JSON.stringify(updatedUser));
        setUser(updatedUser);
    };

    const forgotPassword = async (email) => {
        const res = await authAPI.forgotPassword(email);
        return res.data;
    };

    const resetPassword = async (email, code, password) => {
        const res = await authAPI.resetPassword(email, code, password);
        return res.data;
    };

    return (
        <AuthContext.Provider value={{ user, loading, login, register, verifyEmail, resendVerification, verifyAdminMfa, forgotPassword, resetPassword, logout, updateUser }}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
